import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  LinearProgress,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Icon } from '@iconify/react';
import type {
  SearchConnectionView,
  SearchOverview,
  SearchProvider,
  SearchSiteView,
} from '../../../api/src/search-metrics/search-metrics.types';
import { api } from '../api/client';
import { EditorOnly } from '../components/EditorOnly';
import { Box, Stack, Typography } from '../components/mui-system';
import { PageHeader } from '../components/PageHeader';
import { useWorkspace } from '../workspace-context';
import { MetricsConnectionsDialog } from './MetricsConnectionsDialog';
import { MetricsDetailDialog } from './MetricsDetailDialog';
import { MetricsSiteCard, type SiteCardMode } from './MetricsSiteCard';
import { MetricsLoaderCard } from './MetricsLoaderCard';
import { metricsLoaderState } from './metrics-loader-state';
import { siteSortNames, sortMetricSites, type SiteSort } from './metrics-site-order';
import {
  MetricsChartLegend,
  MetricsValues,
  chartModeNames,
  readableDate,
  sourceColors,
  sourceNames,
  type ChartMode,
} from './search-metrics-presentation';

function daysAgo(days: number) {
  return new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
}
const cardModeKey = 'seo-dashboard.metrics-card-mode';
const siteSortKey = 'seo-dashboard.metrics-site-sort';
export function MetricsPage() {
  const { activeWorkspace, workspaces, selectWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace!.id;
  const [dateFrom, setDateFrom] = useState(() => daysAgo(30));
  const [dateTo, setDateTo] = useState(() => daysAgo(3));
  const [provider, setProvider] = useState<SearchProvider | 'all'>('all');
  const [connectionId, setConnectionId] = useState('');
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState<SearchConnectionView[]>([]);
  const [data, setData] = useState<SearchOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [observedAt, setObservedAt] = useState<string | null>(null);
  const [statusStale, setStatusStale] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [connectionsOpen, setConnectionsOpen] = useState(false);
  const [detail, setDetail] = useState<SearchSiteView | null>(null);
  const [kind, setKind] = useState<ChartMode>('traffic');
  const [cardMode, setCardMode] = useState<SiteCardMode>(() =>
    localStorage.getItem(cardModeKey) === 'mini' ? 'mini' : 'large',
  );
  const [siteSort, setSiteSort] = useState<SiteSort>(() => {
    const saved = localStorage.getItem(siteSortKey);
    return saved === 'clicks' || saved === 'impressions' ? saved : 'domain';
  });
  const sites = useMemo(() => sortMetricSites(data?.sites ?? [], siteSort), [data, siteSort]);
  useEffect(() => localStorage.setItem(cardModeKey, cardMode), [cardMode]);
  useEffect(() => localStorage.setItem(siteSortKey, siteSort), [siteSort]);
  const generation = useRef(0);
  const validPeriod = Boolean(
    dateFrom &&
    dateTo &&
    dateFrom <= dateTo &&
    dateTo <= daysAgo(0) &&
    Date.parse(dateTo) - Date.parse(dateFrom) < 500 * 86400000,
  );
  const load = useCallback(async () => {
    const current = ++generation.current;
    if (!validPeriod) {
      setLoading(false);
      return;
    }
    try {
      const value = await api.searchMetrics(workspaceId, {
        dateFrom,
        dateTo,
        provider,
        ...(connectionId ? { connectionId } : {}),
        ...(search ? { search } : {}),
      });
      if (current === generation.current) {
        setData(value);
        setObservedAt(new Date().toISOString());
        setStatusStale(false);
        setAccounts(value.connections);
        setError('');
      }
    } catch (e) {
      if (current === generation.current) {
        setStatusStale(true);
        setError(e instanceof Error ? e.message : 'Не удалось загрузить метрики');
      }
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [workspaceId, dateFrom, dateTo, provider, connectionId, search, validPeriod]);
  useEffect(() => {
    setLoading(true);
    setData(null);
    void load();
    return () => {
      generation.current++;
    };
  }, [load]);
  const loader = metricsLoaderState(data?.sites ?? []);
  const active = loader.active;
  useEffect(() => {
    if (!validPeriod) return;
    let pending = false;
    const poll = async () => {
      if (pending || document.visibilityState === 'hidden') return;
      pending = true;
      try {
        await load();
      } finally {
        pending = false;
      }
    };
    const wake = () => void poll();
    const timer = window.setInterval(wake, active ? 2500 : 30_000);
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
    };
  }, [active, load, validPeriod]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    if (connected) {
      if (connected !== workspaceId && workspaces.some((w) => w.id === connected)) {
        selectWorkspace(connected);
        return;
      }
      setNotice('Аккаунт подключён. Выберите сайты и нажмите «Загрузить данные».');
      setConnectionsOpen(true);
      window.history.replaceState(null, '', '/metrics');
    }
    if (params.has('connectionError')) {
      setError(
        'Не удалось завершить подключение. Проверьте разрешения приложения и повторите вход.',
      );
      window.history.replaceState(null, '', '/metrics');
    }
  }, [workspaceId, workspaces, selectWorkspace]);
  async function sync() {
    setBusy(true);
    setError('');
    try {
      await api.syncSearchMetrics(workspaceId, {
        dateFrom,
        dateTo,
        provider,
        ...(connectionId ? { connectionId } : {}),
        ...(search ? { search } : {}),
      });
      setNotice('Выбранный и предыдущий периоды добавлены в очередь загрузки.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось начать загрузку');
    } finally {
      setBusy(false);
    }
  }
  function preset(days: number) {
    const end = daysAgo(3);
    setDateTo(end);
    setDateFrom(new Date(Date.parse(end) - (days - 1) * 86400000).toISOString().slice(0, 10));
  }
  return (
    <>
      <PageHeader
        title="Метрики"
        description="Показы, клики и динамика сайтов в Google и Яндексе."
        action={
          <>
            <Button
              variant="outlined"
              startIcon={<Icon icon="solar:link-circle-linear" />}
              disabled={!data}
              onClick={() => setConnectionsOpen(true)}
            >
              Подключения
            </Button>
            <EditorOnly permissions="metrics.run">
              <Button
                variant="contained"
                disabled={busy || loading || !validPeriod || !data?.sites.length}
                onClick={() => void sync()}
                startIcon={<Icon icon="solar:refresh-linear" />}
              >
                {busy ? 'Добавляем в очередь…' : 'Загрузить данные'}
              </Button>
            </EditorOnly>
          </>
        }
      />
      <Stack gap={2.5}>
        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}{' '}
            <Button size="small" onClick={() => void load()}>
              Повторить
            </Button>
          </Alert>
        )}
        {notice && (
          <Alert severity="info" onClose={() => setNotice('')}>
            {notice}
          </Alert>
        )}
        <Card variant="outlined">
          <CardContent>
            <Stack gap={2}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                gap={1.5}
                alignItems={{ sm: 'center' }}
              >
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={provider}
                  onChange={(_, value: SearchProvider | 'all' | null) => {
                    if (value) {
                      setProvider(value);
                      setConnectionId('');
                    }
                  }}
                  aria-label="Источник статистики"
                >
                  <ToggleButton value="all">Все источники</ToggleButton>
                  <ToggleButton value="google">Google</ToggleButton>
                  <ToggleButton value="yandex">Яндекс</ToggleButton>
                </ToggleButtonGroup>
                <TextField
                  select
                  size="small"
                  label="Аккаунт"
                  value={connectionId}
                  onChange={(e) => setConnectionId(e.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem value="">Все аккаунты</MenuItem>
                  {accounts
                    .filter((c) => provider === 'all' || c.provider === provider)
                    .map((c) => (
                      <MenuItem value={c.id} key={c.id}>
                        {c.label}
                      </MenuItem>
                    ))}
                </TextField>
                <TextField
                  size="small"
                  label="Найти сайт"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ ml: { sm: 'auto' }, minWidth: 180 }}
                />
              </Stack>
              <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                {[7, 28, 90, 180, 365, 480].map((days) => (
                  <Button
                    key={days}
                    size="small"
                    variant={
                      Math.round((Date.parse(dateTo) - Date.parse(dateFrom)) / 86400000) + 1 ===
                      days
                        ? 'contained'
                        : 'outlined'
                    }
                    onClick={() => preset(days)}
                  >
                    {days === 7 || days === 28
                      ? `${days} дней`
                      : days === 365
                        ? '12 мес.'
                        : `${days / 30} мес.`}
                  </Button>
                ))}
                <TextField
                  size="small"
                  type="date"
                  label="Начало периода"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Конец периода"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Stack>
              {data && (
                <Typography color="text.secondary" fontSize={12}>
                  Сравнение с {readableDate(data.previousPeriod.dateFrom)} —{' '}
                  {readableDate(data.previousPeriod.dateTo)}. Последние дни могут поступать с
                  задержкой.
                </Typography>
              )}
              {!validPeriod && (
                <Alert severity="warning">Выберите период до 500 дней без будущих дат.</Alert>
              )}
            </Stack>
          </CardContent>
        </Card>
        {loading && <LinearProgress aria-label="Загрузка метрик" />}
        {data && <MetricsLoaderCard state={loader} observedAt={observedAt} stale={statusStale} />}
        {data && (
          <>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: '1fr', lg: provider === 'all' ? '1fr 1fr' : '1fr' }}
              gap={2}
            >
              {data.summary.map((summary) => (
                <Card variant="outlined" key={summary.provider}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" gap={1} mb={2}>
                      <Box
                        width={9}
                        height={9}
                        sx={{ borderRadius: '50%', bgcolor: sourceColors[summary.provider] }}
                      />
                      <Typography fontWeight={700}>{sourceNames[summary.provider]}</Typography>
                      <Typography fontSize={12} color="text.secondary">
                        {summary.siteCount} сайтов
                      </Typography>
                    </Stack>
                    <MetricsValues value={summary.current} change={summary.clickChange} summary />
                  </CardContent>
                </Card>
              ))}
            </Box>
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h5">
                Сайты{' '}
                <Typography component="span" color="text.secondary">
                  {data.sites.length}
                </Typography>
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                <TextField
                  select
                  size="small"
                  label="Сортировка"
                  value={siteSort}
                  onChange={(e) => setSiteSort(e.target.value as SiteSort)}
                  sx={{ minWidth: 210 }}
                >
                  {(Object.keys(siteSortNames) as SiteSort[]).map((sort) => (
                    <MenuItem key={sort} value={sort}>
                      {siteSortNames[sort]}
                    </MenuItem>
                  ))}
                </TextField>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={cardMode}
                  onChange={(_, value: SiteCardMode | null) => {
                    if (value) setCardMode(value);
                  }}
                  aria-label="Размер карточек"
                >
                  <ToggleButton value="large">Большой</ToggleButton>
                  <ToggleButton value="mini">Мини</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              gap={1}
              flexWrap="wrap"
              alignItems="center"
              justifyContent="space-between"
            >
              <ToggleButtonGroup
                size="small"
                exclusive
                value={kind}
                onChange={(_, value: ChartMode | null) => {
                  if (value) setKind(value);
                }}
                aria-label="Показатель графика"
                sx={{ flexWrap: 'wrap' }}
              >
                {(Object.keys(chartModeNames) as ChartMode[]).map((k) => (
                  <ToggleButton key={k} value={k}>
                    {chartModeNames[k]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {siteSort !== 'domain' && (
                <Typography color="text.secondary" fontSize={12} sx={{ maxWidth: 440 }}>
                  По сумме видимых источников за период. Неполные периоды не пересчитываются;
                  неизвестные значения — в конце.
                </Typography>
              )}
            </Stack>
            {!data.sites.length && (
              <Card variant="outlined">
                <CardContent sx={{ py: 5, textAlign: 'center' }}>
                  <Typography variant="h5">
                    {data.connections.length
                      ? 'Нет сайтов для выбранных фильтров'
                      : 'Подключите поисковую статистику'}
                  </Typography>
                  <Typography color="text.secondary" mt={1} mb={2}>
                    Добавьте аккаунты Google Search Console и Яндекс Вебмастера, выберите сайты и
                    загрузите данные.
                  </Typography>
                  <Button variant="outlined" onClick={() => setConnectionsOpen(true)}>
                    Открыть подключения
                  </Button>
                </CardContent>
              </Card>
            )}
            <Box
              display="grid"
              gridTemplateColumns={
                cardMode === 'mini'
                  ? 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))'
                  : {
                      xs: '1fr',
                      md: 'repeat(2,minmax(0,1fr))',
                      xl: 'repeat(3,minmax(0,1fr))',
                    }
              }
              gap={cardMode === 'mini' ? 1.5 : 2}
              alignItems={cardMode === 'mini' ? 'start' : 'stretch'}
            >
              {sites.map((site) => (
                <MetricsSiteCard
                  key={site.siteKey}
                  site={site}
                  period={data.period}
                  kind={kind}
                  mode={cardMode}
                  onDetails={() => setDetail(site)}
                />
              ))}
            </Box>
            {sites.length > 0 && (
              <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
                <MetricsChartLegend
                  providers={sites.flatMap((site) =>
                    site.sources.map((source) => source.property.provider),
                  )}
                  kind={kind}
                />
              </Box>
            )}
          </>
        )}
      </Stack>
      {connectionsOpen && data && (
        <MetricsConnectionsDialog
          workspaceId={workspaceId}
          overview={data}
          close={() => setConnectionsOpen(false)}
          changed={load}
        />
      )}
      {detail && data && (
        <MetricsDetailDialog
          workspaceId={workspaceId}
          site={data.sites.find((s) => s.siteKey === detail.siteKey) ?? detail}
          period={data.period}
          kind={kind}
          close={() => setDetail(null)}
        />
      )}
    </>
  );
}
